import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { refreshSupabaseConnection } from "@/integrations/supabase/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { useEffect } from "react";
import {
	BrowserRouter,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Checkout from "./pages/Checkout";
import Dashboard from "./pages/Dashboard";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProductDetail from "./pages/ProductDetail";
import Products from "./pages/Products";
import SupplierProfile from "./pages/SupplierProfile";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			staleTime: 30000,
		},
	},
});

// This component monitors route changes to refresh Supabase connection
const RouteChangeListener = () => {
	const location = useLocation();

	useEffect(() => {
		// When route changes, ensure Supabase connection is healthy
		refreshSupabaseConnection();
	}, [location.pathname]);

	return null;
};

// Add this new component above the App component
const SupplierGuard = ({ children }: { children: React.ReactNode }) => {
	const { user, isSupplier } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (user && isSupplier && !location.pathname.startsWith("/dashboard")) {
			navigate("/dashboard");
		}
	}, [user, isSupplier, navigate, location.pathname]);

	return <>{children}</>;
};

const App = () => (
	<QueryClientProvider client={queryClient}>
		<TooltipProvider>
			<AuthProvider>
				<CartProvider>
					<Toaster />
					<Sonner />
					<BrowserRouter>
						<RouteChangeListener />
						<SupplierGuard>
							<Routes>
								<Route path="/" element={<Index />} />
								<Route
									path="/products"
									element={<Products />}
								/>
								<Route
									path="/products/:id"
									element={<ProductDetail />}
								/>
								<Route
									path="/suppliers/:id"
									element={<SupplierProfile />}
								/>
								<Route
									path="/checkout/:supplierId"
									element={<Checkout />}
								/>
								<Route path="/auth" element={<Auth />} />
								<Route
									path="/auth/callback"
									element={<AuthCallback />}
								/>
								<Route
									path="/dashboard/*"
									element={<Dashboard />}
								/>
								<Route path="*" element={<NotFound />} />
							</Routes>
						</SupplierGuard>
					</BrowserRouter>
				</CartProvider>
			</AuthProvider>
		</TooltipProvider>
		<Analytics />
	</QueryClientProvider>
);

export default App;
